import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiService from "../services/api";
import PageShell from "../components/PageShell";
import { useAuth } from "../contexts/AuthContext";

// Topic slug whose card opens the speaking coach instead of an article list.
// Set by service/topics.py when english/stories/*.md are synced.
const ENGLISH_SLUG = "english";

/** The Knowledge Base index — the list of topics. Click one to see its articles. */
export default function TopicsPage() {
  const { isAuthenticated } = useAuth();
  const [topics, setTopics] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    apiService
      .listTopics()
      .then((data) => {
        setTopics(data);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [isAuthenticated]);

  return (
    <PageShell>
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-slate-100">
          Knowledge Base
        </h1>
        <p className="mt-2 text-slate-400">
          Notes and explainers on computer vision, LLMs and systems. Pick a
          topic to browse its articles.
        </p>
        <Link
          to="/graph"
          className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-sm text-sky-400 transition hover:border-sky-500/40 hover:text-sky-300"
        >
          ◇ Knowledge graph — concepts in reading order
        </Link>
      </header>

      {status === "loading" && (
        <p className="text-slate-500">Loading topics…</p>
      )}
      {status === "error" && (
        <p className="text-rose-400">
          Could not load topics. Is the backend running?
        </p>
      )}

      {status === "ready" && topics.length === 0 && (
        <p className="text-slate-500">No topics available.</p>
      )}

      {status === "ready" && topics.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {topics.map((topic) => {
            // English is the one topic whose stories are a tool rather than
            // reading: the card opens the speaking coach, which shows the same
            // stories with their audio, phrases and practice progress. The
            // article view still exists at /articles/topic/english — nothing
            // links to it, because the coach is strictly more useful.
            const isCoach = topic.slug === ENGLISH_SLUG;
            return (
              <Link
                key={topic.slug}
                to={isCoach ? "/english" : `/articles/topic/${topic.slug}`}
                className="group block rounded-xl border border-slate-800 bg-slate-900/40 p-6 transition hover:border-sky-500/40 hover:bg-slate-900/80"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="text-xl font-semibold text-slate-100 transition group-hover:text-sky-300">
                    {isCoach && "🎙 "}
                    {topic.name}
                  </h2>
                  <div className="flex shrink-0 items-center gap-2">
                    {topic.visibility === "private" && (
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-400">
                        🔒 private
                      </span>
                    )}
                    <span className="rounded-full bg-slate-800 px-3 py-0.5 text-xs text-slate-400">
                      {topic.count} {topic.count === 1 ? "article" : "articles"}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-slate-400">
                  {isCoach
                    ? "Speaking coach — read, listen, then practise aloud."
                    : topic.description}
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
